/**
 * Full Team Registration via CSV — add-only.
 * Does not modify RegisterTeam.js. Full Team Registration (form) remains the fallback.
 * Uses the same APIs: POST /api/users/full-team and POST /api/app-roles/*.
 */

import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

const CAP_ROLES = ['CAP Owner', 'CAP Lead', 'CAP Admin', 'CAP Developer', 'CAP Validator', 'CAP Leader'];
const SCRUBBER_ROLES = ['admin', 'supervisor', 'analyst'];
const SCRUBBER_SCOPES = ['ODAG', 'CDAG', 'FA', 'SNPCC'];
const RISK_ASSIGNEES = ['admin', 'supervisor', 'lead'];

const CSV_HEADERS = [
  'department_id',
  'department_name',
  'admin_name',
  'admin_email',
  'admin_password',
  'admin_contract_id',
  'admin_pbp_id',
  'supervisor_name',
  'supervisor_email',
  'supervisor_password',
  'supervisor_contract_id',
  'supervisor_pbp_id',
  'lead_name',
  'lead_email',
  'lead_password',
  'lead_contract_id',
  'lead_pbp_id',
  'risk_dept_admin_assignee',
  'risk_officer_assignee',
  'cap_admin_role',
  'cap_supervisor_role',
  'cap_lead_role',
  'scrubber_admin_role',
  'scrubber_admin_scopes',
  'scrubber_supervisor_role',
  'scrubber_supervisor_scopes',
  'scrubber_lead_role',
  'scrubber_lead_scopes'
];

function escapeCsvCell(s) {
  if (s == null) return '';
  const str = String(s).trim();
  if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function parseCsvLine(line) {
  const out = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      let cell = '';
      i++;
      while (i < line.length) {
        if (line[i] === '"') {
          i++;
          if (line[i] === '"') { cell += '"'; i++; }
          else break;
        } else {
          cell += line[i];
          i++;
        }
      }
      out.push(cell.trim());
    } else {
      let cell = '';
      while (i < line.length && line[i] !== ',') cell += line[i++];
      out.push(cell.trim());
      if (line[i] === ',') i++;
    }
  }
  return out;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i]);
    const row = {};
    headers.forEach((h, j) => { row[h] = values[j] != null ? values[j] : ''; });
    rows.push(row);
  }
  return { headers, rows };
}

function mapRowToDepartment(row, rowIndex, _finalizedDeptIds) {
  const departmentId = (row.department_id || '').trim();
  const departmentName = (row.department_name || '').trim();
  const contractAssignments = [];
  const aContract = (row.admin_contract_id || '').trim();
  const aPbp = (row.admin_pbp_id || '').trim();
  if (aContract && aPbp) contractAssignments.push({ contractId: aContract, pbpId: aPbp });
  const supervisorAssignments = [];
  const sContract = (row.supervisor_contract_id || '').trim();
  const sPbp = (row.supervisor_pbp_id || '').trim();
  if (sContract && sPbp) supervisorAssignments.push({ contractId: sContract, pbpId: sPbp });
  const leadAssignments = [];
  const lContract = (row.lead_contract_id || '').trim();
  const lPbp = (row.lead_pbp_id || '').trim();
  if (lContract && lPbp) leadAssignments.push({ contractId: lContract, pbpId: lPbp });
  const riskDeptAdmin = (row.risk_dept_admin_assignee || 'admin').trim().toLowerCase();
  const riskOfficer = (row.risk_officer_assignee || '').trim().toLowerCase();
  const scrubberScopes = (str) => {
    if (!str || !String(str).trim()) return [];
    return String(str).split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
  };
  return {
    rowIndex: rowIndex + 1,
    departmentId,
    departmentName,
    userName: (row.admin_name || '').trim(),
    userEmail: (row.admin_email || '').trim(),
    userPassword: (row.admin_password || '').trim(),
    userRole: 'admin',
    contractAssignments,
    supervisorName: (row.supervisor_name || '').trim(),
    supervisorEmail: (row.supervisor_email || '').trim(),
    supervisorPassword: (row.supervisor_password || '').trim(),
    supervisorRole: 'supervisor',
    supervisorAssignments,
    leadName: (row.lead_name || '').trim(),
    leadEmail: (row.lead_email || '').trim(),
    leadPassword: (row.lead_password || '').trim(),
    leadRole: 'lead',
    leadAssignments,
    capAdminRole: (row.cap_admin_role || '').trim(),
    capSupervisorRole: (row.cap_supervisor_role || '').trim(),
    capLeadRole: (row.cap_lead_role || '').trim(),
    scrubberAdminRole: (row.scrubber_admin_role || '').trim().toLowerCase(),
    scrubberAdminScopes: scrubberScopes(row.scrubber_admin_scopes),
    scrubberSupervisorRole: (row.scrubber_supervisor_role || '').trim().toLowerCase(),
    scrubberSupervisorScopes: scrubberScopes(row.scrubber_supervisor_scopes),
    scrubberLeadRole: (row.scrubber_lead_role || '').trim().toLowerCase(),
    scrubberLeadScopes: scrubberScopes(row.scrubber_lead_scopes),
    riskDeptAdminAssignee: riskDeptAdmin || 'admin',
    riskOfficerAssignee: riskOfficer || ''
  };
}

function validateDepartments(departments, finalizedDeptIds) {
  const errors = [];
  const allowedCap = new Set(CAP_ROLES);
  const allowedScrubber = new Set(SCRUBBER_ROLES);
  const allowedScopes = new Set(SCRUBBER_SCOPES);
  const allowedRisk = new Set(RISK_ASSIGNEES);
  departments.forEach(d => {
    const errs = [];
    if (!d.departmentId) errs.push('department_id required');
    else if (!finalizedDeptIds.has(d.departmentId)) errs.push('department not found or not finalized');
    if (!d.userName) errs.push('admin_name required');
    if (!d.userEmail) errs.push('admin_email required');
    if (!d.userPassword) errs.push('admin_password required');
    if (!d.contractAssignments.length || !d.contractAssignments[0].contractId || !d.contractAssignments[0].pbpId)
      errs.push('admin_contract_id and admin_pbp_id required');
    if (!d.supervisorName) errs.push('supervisor_name required');
    if (!d.supervisorEmail) errs.push('supervisor_email required');
    if (!d.supervisorPassword) errs.push('supervisor_password required');
    if (!d.supervisorAssignments.length || !d.supervisorAssignments[0].contractId || !d.supervisorAssignments[0].pbpId)
      errs.push('supervisor_contract_id and supervisor_pbp_id required');
    if (!d.leadName) errs.push('lead_name required');
    if (!d.leadEmail) errs.push('lead_email required');
    if (!d.leadPassword) errs.push('lead_password required');
    if (!d.leadAssignments.length || !d.leadAssignments[0].contractId || !d.leadAssignments[0].pbpId)
      errs.push('lead_contract_id and lead_pbp_id required');
    if (!allowedRisk.has(d.riskDeptAdminAssignee)) errs.push('risk_dept_admin_assignee must be admin, supervisor, or lead');
    if (d.riskOfficerAssignee && !allowedRisk.has(d.riskOfficerAssignee)) errs.push('risk_officer_assignee must be admin, supervisor, or lead');
    if (d.capAdminRole && !allowedCap.has(d.capAdminRole)) errs.push(`cap_admin_role must be one of: ${CAP_ROLES.join(', ')}`);
    if (d.capSupervisorRole && !allowedCap.has(d.capSupervisorRole)) errs.push(`cap_supervisor_role must be one of: ${CAP_ROLES.join(', ')}`);
    if (d.capLeadRole && !allowedCap.has(d.capLeadRole)) errs.push(`cap_lead_role must be one of: ${CAP_ROLES.join(', ')}`);
    if (d.scrubberAdminRole) {
      if (!allowedScrubber.has(d.scrubberAdminRole)) errs.push('scrubber_admin_role must be admin, supervisor, or analyst');
      if (!d.scrubberAdminScopes.length) errs.push('scrubber_admin_scopes required when scrubber_admin_role is set');
      else if (d.scrubberAdminScopes.some(s => !allowedScopes.has(s))) errs.push('scrubber_admin_scopes must be ODAG, CDAG, FA, SNPCC');
    }
    if (d.scrubberSupervisorRole) {
      if (!allowedScrubber.has(d.scrubberSupervisorRole)) errs.push('scrubber_supervisor_role must be admin, supervisor, or analyst');
      if (!d.scrubberSupervisorScopes.length) errs.push('scrubber_supervisor_scopes required when scrubber_supervisor_role is set');
      else if (d.scrubberSupervisorScopes.some(s => !allowedScopes.has(s))) errs.push('scrubber_supervisor_scopes must be ODAG, CDAG, FA, SNPCC');
    }
    if (d.scrubberLeadRole) {
      if (!allowedScrubber.has(d.scrubberLeadRole)) errs.push('scrubber_lead_role must be admin, supervisor, or analyst');
      if (!d.scrubberLeadScopes.length) errs.push('scrubber_lead_scopes required when scrubber_lead_role is set');
      else if (d.scrubberLeadScopes.some(s => !allowedScopes.has(s))) errs.push('scrubber_lead_scopes must be ODAG, CDAG, FA, SNPCC');
    }
    if (errs.length) errors.push({ rowIndex: d.rowIndex, departmentName: d.departmentName || d.departmentId, errors: errs });
  });
  return errors;
}

export default function RegisterTeamCsv() {
  const [finalizedDepartments, setFinalizedDepartments] = useState([]);
  const [file, setFile] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/departments/with-aliases`, { credentials: 'include' });
        const data = await res.json();
        if (Array.isArray(data)) setFinalizedDepartments(data.filter(d => d.submitted));
      } catch (e) {
        console.error('Failed to load departments', e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (departments.length === 0 || finalizedDepartments.length === 0) return;
    const finalizedIds = new Set(finalizedDepartments.map(d => d.id));
    setValidationErrors(validateDepartments(departments, finalizedIds));
  }, [finalizedDepartments]);

  const downloadTemplate = () => {
    const headerRow = CSV_HEADERS.join(',');
    const exampleRow = [
      '', '', 'Admin Name', 'admin@example.com', 'TempPass1!', '', '', 'Sup Name', 'sup@example.com', 'TempPass1!', '', '',
      'Lead Name', 'lead@example.com', 'TempPass1!', '', '', 'admin', '', 'CAP Admin', '', '', '', 'ODAG,CDAG', '', '', '', ''
    ].map(escapeCsvCell).join(',');
    const csv = [headerRow, exampleRow].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'full_team_registration_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    setMessage('Template downloaded. Fill department_id, contract_id, pbp_id (UUIDs from the app), names, emails, passwords. Optional: CAP roles, Scrubber role+scopes (ODAG,CDAG,FA,SNPCC), Risk assignees.');
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
    setDepartments([]);
    setValidationErrors([]);
    setMessage('');
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { rows } = parseCsv(reader.result);
        const finalizedIds = new Set((finalizedDepartments || []).map(d => d.id));
        const mapped = rows.map((row, i) => mapRowToDepartment(row, i, finalizedIds));
        setDepartments(mapped);
        const errs = validateDepartments(mapped, finalizedIds);
        setValidationErrors(errs);
        if (errs.length === 0 && mapped.length > 0) setMessage(`Parsed ${mapped.length} department(s). Review and click Submit.`);
        else if (errs.length > 0) setMessage(`Validation failed for ${errs.length} row(s). Fix errors and re-upload.`);
        else setMessage('No valid rows in CSV.');
      } catch (err) {
        setMessage('Failed to parse CSV: ' + err.message);
      }
    };
    reader.readAsText(f, 'UTF-8');
  };

  const runSubmitForDepartment = async (department) => {
    const orgId = JSON.parse(localStorage.getItem('sessionUser'))?.organization_id;
    const res = await fetch(`${API_BASE}/users/full-team`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: department.userName,
        email: department.userEmail,
        role: department.userRole,
        password: department.userPassword,
        departmentId: department.departmentId,
        contractAssignments: department.contractAssignments,
        supervisorName: department.supervisorName,
        supervisorEmail: department.supervisorEmail,
        supervisorRole: department.supervisorRole,
        supervisorPassword: department.supervisorPassword,
        leadName: department.leadName,
        leadEmail: department.leadEmail,
        leadRole: department.leadRole,
        leadPassword: department.leadPassword,
        supervisorAssignments: department.supervisorAssignments,
        leadAssignments: department.leadAssignments,
        capAdminRole: department.capAdminRole,
        capSupervisorRole: department.capSupervisorRole,
        capLeadRole: department.capLeadRole
      })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'full-team failed');
    const effectiveCapAdminRole = department.capAdminRole || (department.riskDeptAdminAssignee === 'admin' ? 'CAP Admin' : '');
    const allAssignments = [
      { userId: data.createdUserIds?.admin, role: department.userRole, assignments: department.contractAssignments },
      { userId: data.createdUserIds?.supervisor, role: department.supervisorRole, assignments: department.supervisorAssignments },
      { userId: data.createdUserIds?.lead, role: department.leadRole, assignments: department.leadAssignments }
    ];
    for (const { userId, role, assignments } of allAssignments) {
      for (const a of assignments || []) {
        if (!a.contractId || !a.pbpId) continue;
        const mr = await fetch(`${API_BASE}/app-roles/memo`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            role,
            department_id: department.departmentId,
            contract_id: a.contractId,
            pbp_id: a.pbpId,
            organization_id: orgId
          })
        });
        const mrJson = await mr.json();
        if (!mrJson.success) throw new Error('Memo role: ' + (mrJson.message || mr.statusText));
      }
    }
    if (effectiveCapAdminRole && data.createdUserIds?.admin) {
      for (const a of department.contractAssignments || []) {
        const cr = await fetch(`${API_BASE}/app-roles/cap`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: data.createdUserIds.admin,
            role: effectiveCapAdminRole,
            department_id: department.departmentId || null,
            contract_id: a?.contractId || null,
            organization_id: orgId || ''
          })
        });
        if (!cr.ok) throw new Error('CAP Admin role failed');
      }
    }
    const deptAdminUserId = department.riskDeptAdminAssignee === 'admin' ? data.createdUserIds?.admin : department.riskDeptAdminAssignee === 'supervisor' ? data.createdUserIds?.supervisor : data.createdUserIds?.lead;
    if (!deptAdminUserId) throw new Error('Risk Dept Admin user missing');
    const rdr = await fetch(`${API_BASE}/app-roles/risk`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: deptAdminUserId, role: 'admin', department_id: department.departmentId, scope: 'department' })
    });
    if (!rdr.ok) throw new Error('Risk Dept Admin failed');
    if (department.riskOfficerAssignee) {
      const ou = department.riskOfficerAssignee === 'admin' ? data.createdUserIds?.admin : department.riskOfficerAssignee === 'supervisor' ? data.createdUserIds?.supervisor : data.createdUserIds?.lead;
      const ror = await fetch(`${API_BASE}/app-roles/risk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: ou, role: 'officer', department_id: null, scope: 'org' })
      });
      if (!ror.ok) throw new Error('Risk Officer failed');
    }
    if (department.capSupervisorRole && data.createdUserIds?.supervisor) {
      for (const a of department.supervisorAssignments || []) {
        const cr = await fetch(`${API_BASE}/app-roles/cap`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: data.createdUserIds.supervisor,
            role: department.capSupervisorRole,
            department_id: department.departmentId || null,
            contract_id: a?.contractId || null,
            organization_id: orgId || ''
          })
        });
        if (!cr.ok) throw new Error('CAP Supervisor failed');
      }
    }
    if (department.capLeadRole && data.createdUserIds?.lead) {
      for (const a of department.leadAssignments || []) {
        const cr = await fetch(`${API_BASE}/app-roles/cap`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: data.createdUserIds.lead,
            role: department.capLeadRole,
            department_id: department.departmentId || null,
            contract_id: a?.contractId || null,
            organization_id: orgId || ''
          })
        });
        if (!cr.ok) throw new Error('CAP Lead failed');
      }
    }
    const assignScrubber = async (userId, role, scopes) => {
      if (!userId || !role || !scopes?.length) return;
      const sr = await fetch(`${API_BASE}/app-roles/scrubber`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role, organization_id: orgId || '' })
      });
      if (!sr.ok) throw new Error('Scrubber role failed');
      const ssr = await fetch(`${API_BASE}/app-roles/scrubber/scopes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, protocol_codes: scopes, organization_id: orgId || '' })
      });
      if (!ssr.ok) throw new Error('Scrubber scopes failed');
    };
    if (department.scrubberAdminRole && data.createdUserIds?.admin && department.scrubberAdminScopes?.length) await assignScrubber(data.createdUserIds.admin, department.scrubberAdminRole, department.scrubberAdminScopes);
    if (department.scrubberSupervisorRole && data.createdUserIds?.supervisor && department.scrubberSupervisorScopes?.length) await assignScrubber(data.createdUserIds.supervisor, department.scrubberSupervisorRole, department.scrubberSupervisorScopes);
    if (department.scrubberLeadRole && data.createdUserIds?.lead && department.scrubberLeadScopes?.length) await assignScrubber(data.createdUserIds.lead, department.scrubberLeadRole, department.scrubberLeadScopes);
  };

  const handleSubmit = async () => {
    const errs = validateDepartments(departments, new Set(finalizedDepartments.map(d => d.id)));
    if (errs.length > 0) {
      setValidationErrors(errs);
      setMessage('Fix validation errors before submitting.');
      return;
    }
    if (departments.length === 0) {
      setMessage('No departments to submit.');
      return;
    }
    setSubmitting(true);
    setMessage('');
    let done = 0;
    for (const dept of departments) {
      try {
        await runSubmitForDepartment(dept);
        done++;
        setMessage(`Submitted ${done}/${departments.length}...`);
      } catch (err) {
        setMessage(`❌ Failed at row ${dept.rowIndex} (${dept.departmentName || dept.departmentId}): ${err.message}`);
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(false);
    setMessage(`✅ All ${done} department(s) registered. Users and roles created in Core and synced to Memo, CAP, Risk, and Scrubber.`);
    window.dispatchEvent(new Event('refresh-lead-check'));
    setDepartments([]);
    setValidationErrors([]);
    setFile(null);
  };

  const finalizedIds = new Set(finalizedDepartments.map(d => d.id));
  const canSubmit = departments.length > 0 && validateDepartments(departments, finalizedIds).length === 0 && !submitting;

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 shadow-xl">
      <h2 className="text-2xl font-bold text-white mb-2">Full Team Registration via CSV</h2>
      <p className="text-slate-400 mb-6">
        Upload a CSV to register Admin, Supervisor, and Lead per department. Same result as the form: users created in Core and roles synced to Memo, CAP, Risk, and Scrubber. You can always use <strong className="text-slate-300">Full Team Registration</strong> (form) as fallback.
      </p>
      <div className="flex flex-wrap gap-4 items-center mb-6">
        <button
          type="button"
          onClick={downloadTemplate}
          className="px-4 py-2 rounded-xl bg-slate-600 hover:bg-slate-500 text-white font-medium transition"
        >
          Download template
        </button>
        <label className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium cursor-pointer transition">
          Choose CSV
          <input type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
        </label>
        {file && <span className="text-slate-400">{file.name}</span>}
      </div>
      {message && (
        <div className={`mb-4 p-4 rounded-xl ${message.startsWith('❌') ? 'bg-red-500/20 text-red-200' : message.startsWith('✅') ? 'bg-green-500/20 text-green-200' : 'bg-slate-700/50 text-slate-200'}`}>
          {message}
        </div>
      )}
      {validationErrors.length > 0 && (
        <div className="mb-4 p-4 rounded-xl bg-amber-500/20 text-amber-200">
          <p className="font-medium mb-2">Validation errors:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {validationErrors.map((e, i) => (
              <li key={i}>Row {e.rowIndex} ({e.departmentName || 'no name'}): {e.errors.join('; ')}</li>
            ))}
          </ul>
        </div>
      )}
      {departments.length > 0 && (
        <>
          <p className="text-slate-300 mb-2">Preview ({departments.length} row(s)):</p>
          <div className="overflow-x-auto mb-4 rounded-xl border border-slate-600">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-700/50 text-slate-300">
                <tr>
                  <th className="px-3 py-2">Row</th>
                  <th className="px-3 py-2">Department</th>
                  <th className="px-3 py-2">Admin email</th>
                  <th className="px-3 py-2">Supervisor email</th>
                  <th className="px-3 py-2">Lead email</th>
                </tr>
              </thead>
              <tbody className="text-slate-200">
                {departments.map((d, i) => (
                  <tr key={i} className="border-t border-slate-600">
                    <td className="px-3 py-2">{d.rowIndex}</td>
                    <td className="px-3 py-2">{d.departmentName || d.departmentId}</td>
                    <td className="px-3 py-2">{d.userEmail}</td>
                    <td className="px-3 py-2">{d.supervisorEmail}</td>
                    <td className="px-3 py-2">{d.leadEmail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="px-6 py-2 rounded-xl bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </>
      )}
    </div>
  );
}
